import { useMutation } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { selectClient } from '@client/model';
import { useToast } from '@client/components/hooks/use-toast';
import { TriggerUnderwritingRequest, openAiIntegrationApi } from '@client/api/waterlily';
import { FundingPolicyType, ApiExError } from '@shared';

export function useTriggerUnderwriting() {
  const client = useSelector(selectClient);
  const clientId = client?.clientId;
  const { toast } = useToast();

  const mutation = useMutation({
    mutationFn: ({
      fundingPolicyType,
      formData,
    }: {
      fundingPolicyType: FundingPolicyType;
      formData?: PolicyFormSubmissionData;
    }) => {
      if (!clientId) {
        throw new Error('Client ID is required');
      }

      const requestBody: TriggerUnderwritingRequest = {
        clientId,
        fundingPolicyType,
        formData,
      };

      return openAiIntegrationApi.triggerUnderwriting(requestBody);
    },
    onSuccess: data => {
      toast({
        title: 'Underwriting triggered successfully',
        description: `Calculating underwriting results. This may take a few moments.`,
        variant: 'default',
        duration: 5000,
      });
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error?.message;

      toast({
        title: 'Failed to trigger underwriting',
        description: message || 'An unexpected error occurred. Please try again.',
        variant: 'destructive',
        duration: 10000,
      });
    },
  });

  // Extract status code from error if it's an ApiExError
  const statusCode = mutation.error instanceof ApiExError ? mutation.error.statusCode : null;

  return {
    ...mutation,
    statusCode,
  };
}
